import { useState, useEffect, useRef } from 'react';
import ExternalNewsCard from './external-news-card.component';
import { trendingTopicsApi } from '../../../../apis/trending-topics.api';
import { transformTopicToNewsItem } from '../../../../utils/news-trending-stories-transformer';

import NewsGreenIcon from '../../../../assets/news-green-icon.svg';
import SearchIcon from '../../../../assets/search-icon.svg';
import EmptyArticleComponent from '../../../EmptyArticleComponent';

const NewsHubComponent = ({ loading, setLoading }) => {
  const [newsItems, setNewsItems] = useState([]);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // Scroll functionality for category tabs
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [listTopic] = useState([
    { id: 'All', name: 'All' },
    { id: 'Technology', name: 'Technology' },
    { id: 'Politics', name: 'Politics' },
    { id: 'Health', name: 'Health' },
    { id: 'Environment', name: 'Environment' },
    { id: 'Business', name: 'Business' },
    { id: 'Science', name: 'Science' },
    { id: 'Sports', name: 'Sports' }
  ]);

  // Debounce search query to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch story pulse from real API with category filtering and search
        const result = await trendingTopicsApi.getPublishedTrendingTopics(
          selectedCategory,
          10,
          debouncedSearchQuery
        );

        if (result.success && result.data) {
          // Transform story pulse data to match news card structure
          const transformedNews = result.data
            .map((topic) => transformTopicToNewsItem(topic))
            .sort((a, b) => b.timestamp - a.timestamp); // Sort by newest first

          setNewsItems(transformedNews);
        } else {
          setError(result.error || 'Failed to load trending stories');
          setNewsItems([]);
        }
      } catch (err) {
        console.error('Error fetching trending stories:', err);
        setError('Failed to load trending stories. Please try again.');
        setNewsItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [selectedCategory, debouncedSearchQuery]); // Removed setLoading from dependencies

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  // Scroll functionality
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      const hasOverflow = scrollWidth > clientWidth;

      if (hasOverflow) {
        // Update both arrows based on scroll position
        setShowLeftArrow(scrollLeft > 0);
        setShowRightArrow(scrollLeft + clientWidth < scrollWidth);
      } else {
        // If no overflow, hide both arrows
        setShowLeftArrow(false);
        setShowRightArrow(false);
      }
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
      // Check scroll position after scrolling
      setTimeout(() => handleScroll(), 100);
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
      // Check scroll position after scrolling
      setTimeout(() => handleScroll(), 100);
    }
  };

  const updateButtonVisibility = () => {
    if (scrollContainerRef.current) {
      const { scrollWidth, clientWidth } = scrollContainerRef.current;
      const hasOverflow = scrollWidth > clientWidth;

      if (!hasOverflow) {
        // If no overflow, hide both arrows
        setShowLeftArrow(false);
        setShowRightArrow(false);
      }
      // Don't set right arrow to true here, let handleScroll manage it
    }
  };

  // Add scroll event listener to check scroll position
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      handleScroll();
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  // Check initial overflow state and on window resize
  useEffect(() => {
    // Add a small delay to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      updateButtonVisibility();
    }, 100);

    window.addEventListener('resize', updateButtonVisibility);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateButtonVisibility);
    };
  }, [listTopic]);

  // Additional effect to handle component mount/unmount scenarios
  useEffect(() => {
    const checkVisibility = () => {
      if (scrollContainerRef.current && listTopic.length > 0) {
        handleScroll();
      }
    };

    // Check immediately
    checkVisibility();

    // Check again after a short delay to handle any async rendering
    const timer = setTimeout(checkVisibility, 200);

    return () => clearTimeout(timer);
  }, [listTopic]);

  if (loading) {
    return (
      <div className="flex flex-col gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-6 bg-gray-300 rounded"></div>
              <div className="w-20 h-4 bg-gray-300 rounded"></div>
              <div className="w-24 h-4 bg-gray-300 rounded"></div>
            </div>
            <div className="h-6 bg-gray-300 rounded mb-2"></div>
            <div className="h-4 bg-gray-300 rounded mb-4"></div>
            <div className="flex items-center gap-4">
              <div className="w-16 h-4 bg-gray-300 rounded"></div>
              <div className="w-12 h-4 bg-gray-300 rounded"></div>
              <div className="w-12 h-4 bg-gray-300 rounded"></div>
              <div className="w-12 h-4 bg-gray-300 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return <EmptyArticleComponent categoryType={'newsHub'} />;
  }

  return (
    <div className="w-full">
      {/* News Hub Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-3">
          <h2 className="text-4xl font-extrabold text-growth-green">News Hub</h2>
          <img src={NewsGreenIcon} alt="News Green Icon" className="w-8 h-8" />
        </div>
        <p className="text-02-dim-gray font-semibold text-xl leading-relaxed">
          Trending Stories from across the internet, view and write your perspective of what's
          happening across the world!
        </p>
      </div>

      {/* Category Tabs */}
      <div className="relative mb-6 w-screen overflow-auto">
        {showLeftArrow && (
          <button
            onClick={scrollLeft}
            id="topic-container-left-arrow-btn"
            className="absolute font-bold bg-white top-1/2 transform -translate-y-1/2 left-[-1px] z-10 h-[40px] pr-[8px] flex items-center"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}

        {/* Scrollable Topic Container */}
        <div className="overflow-x-auto scrollbar-hide pr-8" ref={scrollContainerRef}>
          <div className="flex items-center gap-1 md:gap-4">
            {listTopic.map((topic) => (
              <button
                key={topic.id}
                onClick={() => handleCategoryChange(topic.id)}
                className={`flex items-center gap-2 px-3 py-[5px] rounded-full whitespace-nowrap text-sm font-extrabold transition-all duration-200 flex-shrink-0 ${
                  selectedCategory === topic.id
                    ? 'bg-growth-green text-growth-green-900 border-2 border-growth-green'
                    : 'bg-[#C8E6C9] text-[#204A22] border-2 border-transparent hover:bg-green-100 hover:border-green-200'
                }`}
              >
                {topic.name}
              </button>
            ))}
          </div>
        </div>

        {showRightArrow && (
          <button
            onClick={scrollRight}
            id="topic-container-right-arrow-btn"
            className="absolute font-bold bg-white top-1/2 transform -translate-y-1/2 right-[-2px] z-10 h-[40px] pl-[8px] flex items-center"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search stories"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-[36px] w-full px-4 py-3 pl-12 pr-4 border-2 border-gray-200 rounded-lg focus:border-growth-green focus:outline-none text-gray-700 placeholder-gray-400 transition-colors duration-200"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <img src={SearchIcon} alt="Search Icon" className="w-4 h-4" />
          </div>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
        {/* Search Results Indicator */}
        {debouncedSearchQuery && (
          <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
            <img src={SearchIcon} alt="Search Icon" className="w-4 h-4" />
            <span>
              Search results for "{debouncedSearchQuery}" • {newsItems.length}{' '}
              {newsItems.length === 1 ? 'story' : 'stories'} found
            </span>
            <button
              onClick={() => setSearchQuery('')}
              className="text-growth-green hover:text-growth-green-900 underline"
            >
              Clear search
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-6">
        {newsItems.length > 0 ? (
          newsItems.map((item) => <ExternalNewsCard key={item.id} item={item} />)
        ) : debouncedSearchQuery ? (
          <div className="text-center py-12">
            <div className="mb-4 flex justify-center">
              <img src={SearchIcon} alt="Search Icon" className="w-12 h-12" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No stories found</h3>
            <p className="text-gray-500 mb-4">
              No stories found matching "{debouncedSearchQuery}". Try adjusting your search terms.
            </p>
            <button
              onClick={() => setSearchQuery('')}
              className="px-4 py-2 bg-growth-green text-white rounded-lg hover:opacity-80 transition-opacity"
            >
              Clear search
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default NewsHubComponent;
